import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';
import http from '@/services/http';
import logger from '@/services/logger';
import parseGroupOrderDetails from '@/helpers/parse-group-order-details';
import parseLocation from '@/helpers/parse-location';
import parseYourPayment from '@/helpers/parse-your-payment';
import parseUserLocationType from '@/helpers/parse-user-location-type';

export default {
  namespaced: true,

  state: {
    receipt: {
      applePassUrl: null,
      delivery: {},
      deliveryCharges: 0,
      discount: 0,
      email: null,
      externalOrderId: null,
      fulfillmentType: null,
      googleWalletJwt: null,
      groupOrder: null,
      guestName: null,
      includeUtensils: false,
      isLinkedToCurrentAuthenticatedUser: false,
      isLoading: false,
      locationType: null,
      locationTypeDescription: null,
      loyaltyEngagementSummary: null,
      menuType: null,
      offers: [],
      orderDate: null,
      orderId: null,
      orderItemTotal: 0,
      orderItems: [],
      orderFees: [],
      orderLevelInstructions: null,
      orderType: null,
      payments: [],
      phoneNumber: null,
      pickupDate: null,
      pickupInstructions: null,
      pickupLocation: null,
      redemptionQrCodeValue: null,
      serviceCharges: 0,
      shipment: {},
      showRedemptionQrCode: false,
      store: {},
      sublocationId: null,
      subtotal: 0,
      tax: 0,
      ticketNumber: null,
      tip: 0,
      total: 0,
      totalWithTipAndCharges: 0,
      submittedSurveyId: null
    },
    receiptIsSending: null
  },

  getters: {
    receipt(state, getters, rootState) {
      const computed = {
        groupOrder: parseGroupOrderDetails({
          merchantId: rootState.session.route.merchantId,
          order: state.receipt
        })
      };

      return {
        ...state.receipt,
        ...computed
      };
    },

    userLocationType(state) {
      return parseUserLocationType(state.receipt.locationType);
    },

    yourPayment(state, getters, rootState, rootGetters) {
      const order = rootGetters['orders/order'];
      const payments = state.receipt.payments.length > 0 ? state.receipt.payments : order.payments;
      return parseYourPayment(payments);
    }
  },

  actions: {
    async getReceipt({ commit, rootGetters }, options) {
      const { orderId, guestId, receiptToken, walletType } = options;

      commit('SET_RECEIPT_LOADING_STATUS', true);

      try {
        let order;

        if (orderId && guestId) {
          const response = await http.get(`/cardfree-api/v1/account/me/order/${orderId}`, {
            params: {
              groupMemberGuestId: guestId
            }
          });

          order = response.data;
        }

        else {
          const response = await http.get(`/cardfree-api/v1/receipt/${receiptToken}`, {
            params: {
              includePayments: true,
              walletType
            }
          });

          order = response.data.order;
        }

        const locationResponse = await http.get('/cardfree-api/v1/location/store', {
          params: {
            ids: order.store.storeId
          }
        });

        order.store = parseLocation({
          location: locationResponse.data.data[0],
          orderDetails: rootGetters['session/orderDetails']
        });

        commit('SET_RECEIPT', order);
      }

      catch (error) {
        errorHandlers.critical(error);
      }

      finally {
        commit('SET_RECEIPT_LOADING_STATUS', false);
      }
    },

    async getPatReceipt({ commit, dispatch, getters }, options) {
      try {
        commit('orders/SET_ORDER_LOADING_STATUS', true, { root: true });

        const requestOptions = {};
        if (options.walletType) requestOptions.params = { walletType: options.walletType };

        const { data: { order } } = await http.get(`/cardfree-api/v1/pat/receipt/${options.receiptToken}`, requestOptions);

        commit('orders/SET_ORDER', order, { root: true });

        dispatch('socket/updateOrderRoomWithReceipt', {
          order,
          yourPayment: getters.yourPayment
        }, { root: true });

        commit('orders/SET_ORDER_LOADING_STATUS', false, { root: true });

        if (order.orderState === 'Closed') {
          dispatch('orders/syncOrdersAtTableIntervalStop', null, { root: true });
        }

        await dispatch('locations/selectLocation', { location: order.store }, { root: true });
      }

      catch (error) {
        commit('orders/SET_ORDER_LOADING_STATUS', false, { root: true });

        throw error;
      }
    },

    async reloadPatReceipt({ dispatch, rootGetters }) {
      const orderRoom = rootGetters['socket/orderRoom'];
      const order = rootGetters['orders/order'];

      if (orderRoom.you.paid && !orderRoom.youClosedOrder && order.orderState !== 'Closed') {
        try {
          await dispatch('getPatReceipt', {
            receiptToken: orderRoom.you.receiptToken
          });
        }

        catch (error) {
          logger.error(error);
        }
      }
    },

    async sendReceipt({ commit }, options) {
      try {
        commit('SET_RECEIPT_SENDING_STATUS', true);

        await http.post(`/cardfree-api/v1/receipt/send/${options.receiptToken}`, {
          channelType: options.channelType,
          communicationName: options.communicationName,
          emailAddress: options.email,
          mobileNumber: options.phone
        });

        if (!options.silent) {
          events.emit('notification', {
            type: 'success',
            message: 'Your receipt was sent.'
          });
        }
      }

      catch (error) {
        if (options.silent) {
          errorHandlers.silent(error);
        }

        else {
          errorHandlers.generic(error);
        }
      }

      finally {
        commit('SET_RECEIPT_SENDING_STATUS', false);
      }
    }
  },

  mutations: {
    SET_RECEIPT(state, receipt) {
      const orderId = receipt.orderId ? receipt.orderId.toString() : '';

      state.receipt.applePassUrl = receipt.applePassUrl;
      state.receipt.delivery = receipt.delivery;
      state.receipt.deliveryCharges = receipt.deliveryCharges;
      state.receipt.discount = receipt.discount;
      state.receipt.email = receipt.email;
      state.receipt.externalOrderId = receipt.externalOrderId;
      state.receipt.fulfillmentType = receipt.fulfillmentType;
      state.receipt.googleWalletJwt = receipt.googleWalletJwt;
      state.receipt.groupOrder = receipt.groupOrder;
      state.receipt.guestName = receipt.guestName;
      state.receipt.includeUtensils = receipt.includeUtensils || false;
      state.receipt.isLinkedToCurrentAuthenticatedUser = receipt.isLinkedToCurrentAuthenticatedUser || false;
      state.receipt.locationType = receipt.locationType;
      state.receipt.locationTypeDescription = receipt.locationTypeDescription;
      state.receipt.loyaltyEngagementSummary = receipt.loyaltyEngagementSummary || null;
      state.receipt.menuType = receipt.menuType;
      state.receipt.offers = receipt.offers || [];
      state.receipt.orderDate = receipt.orderDate;
      state.receipt.orderId = receipt.orderId;
      state.receipt.orderItems = receipt.orderItems || [];
      state.receipt.orderFees = receipt.orderFees || [];
      state.receipt.orderItemTotal = receipt.orderItemTotal || 0;
      state.receipt.orderLevelInstructions = receipt.orderLevelInstructions;
      state.receipt.orderType = receipt.orderType;
      state.receipt.payments = receipt.payments || [];
      state.receipt.phoneNumber = receipt.phoneNumber;
      state.receipt.pickupDate = receipt.pickupDate;
      state.receipt.pickupInstructions = receipt.pickupInstructions;
      state.receipt.pickupLocation = receipt.pickupLocation;
      state.receipt.redemptionQrCodeValue = receipt.redemptionQrCodeValue;
      state.receipt.serviceCharges = receipt.serviceCharges;
      state.receipt.shipment = receipt.shipment;
      state.receipt.showRedemptionQrCode = receipt.showRedemptionQrCode;
      state.receipt.store = receipt.store;
      state.receipt.sublocationId = receipt.sublocationId;
      state.receipt.subtotal = receipt.subtotal;
      state.receipt.tax = receipt.tax;
      state.receipt.ticketNumber = receipt.ticketNumber || orderId.substr(-3);
      state.receipt.tip = receipt.tip;
      state.receipt.total = ((parseFloat(receipt.total.toFixed(2)) * 100) + (parseFloat(receipt.tip.toFixed(2)) * 100)) / 100;
      state.receipt.totalWithTipAndCharges = receipt.totalWithTipAndCharges;
      state.receipt.submittedSurveyId = receipt.submittedSurveyId || null;

      // TODO: Sync with BE devs to refine receipt totals calculations
      if (receipt.delivery) {
        state.receipt.total += receipt.delivery.fee;
        state.receipt.total += receipt.delivery.tip;
        state.receipt.tip += receipt.delivery.tip;
      }
      else if (receipt.deliveryCharges) {
        state.receipt.total = ((parseFloat(state.receipt.total.toFixed(2)) * 100) + (parseFloat(receipt.deliveryCharges.toFixed(2)) * 100)) / 100;
      }

      if (receipt.shipment) {
        state.receipt.total = ((parseFloat(state.receipt.total.toFixed(2)) * 100) + (parseFloat(receipt.shipment.fee.toFixed(2)) * 100)) / 100;
      }

      const surchargeAmount = receipt.payments.find(payment => payment.surcharge)?.surcharge;
      if (surchargeAmount) {
        state.receipt.total = ((parseFloat(state.receipt.total.toFixed(2)) * 100) + (parseFloat(surchargeAmount.toFixed(2)) * 100)) / 100;
      }
    },

    SET_RECEIPT_LOADING_STATUS(state, isLoading) {
      state.receipt.isLoading = isLoading;
    },

    SET_RECEIPT_SENDING_STATUS(state, status) {
      state.receiptIsSending = status;
    },

    UPDATE_ORDER_ITEM(state, options) {
      const orderItem = state.receipt.orderItems.find(item => item.orderItemId === options.orderItemId);

      if (!orderItem) {
        return;
      }

      Object.assign(orderItem, options.params);
    }
  }
};
